import { forwardRef, useId, type InputHTMLAttributes } from "react";
import { Check } from "@/shared/components/icons";
import { cn } from "@/shared/lib/cn";

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string | React.ReactNode;
  error?: string | undefined;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, className, id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hasError = Boolean(error);

    return (
      <div className="flex flex-col gap-1">
        <label
          className={cn(
            "flex cursor-pointer items-start gap-3 select-none",
            props.disabled && "cursor-not-allowed opacity-60"
          )}
        >
          <div className="relative flex items-center justify-center pt-0.5">
            <input
              ref={ref}
              aria-describedby={hasError ? errorId : undefined}
              aria-invalid={hasError}
              className={cn(
                "peer h-5 w-5 shrink-0 cursor-pointer appearance-none rounded border-2 transition-colors",
                "focus:ring-2 focus:ring-offset-1 focus:outline-none",
                hasError
                  ? "border-red-500 focus:ring-red-200"
                  : "focus:ring-brand-primary/20 border-gray-300",
                "checked:border-brand-primary checked:bg-brand-primary",
                "disabled:cursor-not-allowed disabled:opacity-50",
                className
              )}
              id={inputId}
              type="checkbox"
              {...props}
            />
            <Check
              aria-hidden="true"
              className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100"
              strokeWidth={3}
            />
          </div>
          {label && <span className="text-sm text-gray-700">{label}</span>}
        </label>
        {error && (
          <p className="text-sm text-red-600" id={errorId}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";
